import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useDispatch } from 'react-redux'
import { change, Field } from 'redux-form'
// hooks
import { useCurrentForm, useShowModal } from 'hooks/index'
// constants
import { Forms, Modals } from 'constants/index'
// types
import { RightSectionRow } from 'components/Modals/MatrixModal/components/Matrix/types'
import { FieldType, IField } from 'types/graphql'
import { WrappedFieldProps } from 'redux-form/lib/Field'
// context
import { ImportedRiskContext } from 'components/Modals/ImportedRiskModal'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// views
import { CloseIcon, MatrixFieldContainer, MatrixLabel } from './views'
// icons
import { AsteriskIcon } from 'components/FormElements/components/InputLabel'
// utils
import { find, flatten, pipe } from 'lodash/fp'

interface Props extends WrappedFieldProps {
  field: IField
  required: boolean
  isTable?: boolean
  isDisabled?: boolean
}

const MatrixField: React.FC<Props> = ({ field, input, required, isDisabled, isTable }) => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { fieldLink } = useCurrentForm()

  const { showModal } = useShowModal({ modalType: Modals.RISK_MATRIX_MODAL })

  const { isImportedField } = React.useContext(ImportedRiskContext)
  const { isRiskTemplate, isRisksRegister, riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const parentField = riskTemplate.fields.find((item) => item.id === field.parentFieldId)

  const isTableField =
    parentField?.fieldType === FieldType.Table ||
    (isRisksRegister && !isImportedField) ||
    !!router.query.multiple ||
    isTable

  const isRegisters = isRisksRegister || !!router.query.multiple

  const selectedOption = pipe(
    flatten,
    find<RightSectionRow>(({ id }) => id === (input.value.value || input.value)),
    (cell) =>
      field.settings.options.find(({ id }) =>
        typeof cell?.value === 'string' ? id === cell?.value : id === cell?.value.id
      )
  )(field.settings.bRightSection)

  const handleShowModal = () => {
    if (isDisabled) return

    showModal({
      field,
      name: input.name,
      value: input.value,
      disabled: !isRiskTemplate || !!fieldLink
    })
  }

  const clearField = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    dispatch(change(Forms.RISK_TEMPLATE_FORM, input.name, null))
  }

  const label = selectedOption
    ? isTableField
      ? selectedOption.label
      : `${field.label} - ${selectedOption.label}`
    : isTableField
      ? '...'
      : field.label

  const showAsteriks = !isRegisters && required && !fieldLink

  if (isRegisters && !selectedOption) {
    return null
  }

  return (
    <MatrixFieldContainer
      disabled={isDisabled}
      isTable={isTableField}
      isRiskTemplate={isRiskTemplate}
      onClick={handleShowModal}
      color={selectedOption?.color}>
      <MatrixLabel>
        {showAsteriks && <AsteriskIcon />}
        {label}
      </MatrixLabel>
      {selectedOption && !isDisabled && <CloseIcon onClick={clearField} />}
    </MatrixFieldContainer>
  )
}

const FieldComponent = (props) => <Field {...props} component={MatrixField} />

export default FieldComponent
